import React, { useMemo, memo } from 'react';
import { Checkbox, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { TimeSignal } from 'graphql/types';
import { useNotesHeaderCheckActions } from 'modules/notes/list/hooks';
import { useFeatureFlag } from 'modules/common/hooks';
import { useMessage } from 'modules/intl';

interface NotesSelectorCounterProps {
  notes: TimeSignal[];
  totalCount: number;
  duration: string;
}

interface CounterTextProps {
  selectedCount: number;
  totalCount: number;
}

const useStyles = makeStyles((theme) => ({
  selectionCount: {
    fontStyle: ({ hasSelections }: { hasSelections: boolean }) =>
      hasSelections ? 'inherit' : 'italic',
    color: ({ hasSelections }: { hasSelections: boolean }) =>
      hasSelections ? theme.palette.text.primary : theme.palette.text.secondary
  }
}));

export const CounterText = ({
  selectedCount,
  totalCount
}: CounterTextProps): React.ReactElement => {
  const values = useMemo(() => ({ selectedCount, totalCount }), [
    selectedCount,
    totalCount
  ]);

  if (selectedCount === 0) {
    return <FormattedMessage id="noteSelector.noneSelected" />;
  }

  return <FormattedMessage id="noteSelector.partialSelected" values={values} />;
};

const NotesSelectorCounterFC: React.FC<NotesSelectorCounterProps> = ({
  notes,
  totalCount,
  duration
}: NotesSelectorCounterProps) => {
  const {
    indeterminate,
    selectAll,
    selectedCount,
    onChange
  } = useNotesHeaderCheckActions({ totalCount, notes });

  const classes = useStyles({ hasSelections: selectedCount > 0 });
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const selectMsg = useMessage('noteSelector.selectAllNotes', {
    duration: duration
  });

  const checkboxInput = useMemo(
    () => ({
      'data-testid': 'clickable_selection-count-checkbox',
      'aria-label': isTiwScreenReaderEnabled ? selectMsg : ''
    }),
    [isTiwScreenReaderEnabled, selectMsg]
  );

  return (
    <Grid
      container
      justifyContent={'flex-start'}
      alignItems={'center'}
      data-testid="notes-selector-counter"
    >
      <Grid item>
        <Checkbox
          data-testid="selection-count-checkbox"
          color="default"
          checked={selectAll}
          indeterminate={indeterminate}
          onChange={onChange}
          inputProps={checkboxInput}
        />
      </Grid>
      <Grid item>
        <Typography
          data-testid="selection-count-text"
          className={classes.selectionCount}
          variant="body2"
        >
          <CounterText selectedCount={selectedCount} totalCount={totalCount} />
        </Typography>
      </Grid>
    </Grid>
  );
};

export const NotesSelectorCounter = memo(NotesSelectorCounterFC);
